import api, { getErrorMessage } from './api';

const authService = {
  /**
   * Login user
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<{token: string}>} - Authentication token
   */
  login: async (email, password) => {
    try {
      const response = await api.post('/api/v1/login/access-token',
        new URLSearchParams({
          username: email,  // OAuth2 uses 'username' field
          password: password,
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Register a new user
   * @param {object} userData - User registration data
   * @param {string} userData.email - User email
   * @param {string} userData.password - User password
   * @param {string} userData.full_name - User full name
   * @returns {Promise<object>} - User data
   */
  register: async (userData) => {
    try {
      const response = await api.post('/api/v1/users/signup', userData);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get current user data
   * @returns {Promise<object>} - Current user data
   */
  getCurrentUser: async () => {
    try {
      const response = await api.get('/api/v1/users/me');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Test if auth token is valid
   * @returns {Promise<object>} - Token validation response
   */
  testToken: async () => {
    try {
      const response = await api.post('/api/v1/login/test-token');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Request password recovery email
   * @param {string} email - User email
   * @returns {Promise<object>} - Recovery response
   */
  recoverPassword: async (email) => {
    try {
      const response = await api.post(`/api/v1/password-recovery/${email}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Reset password with token
   * @param {string} token - Reset token from email
   * @param {string} newPassword - New password
   * @returns {Promise<object>} - Reset response
   */
  resetPassword: async (token, newPassword) => {
    try {
      const response = await api.post('/api/v1/reset-password/', {
        token,
        new_password: newPassword,
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },
};

export default authService;
